import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import nivoTheme from '../../theme/nivoTheme';
import StatusDonut from './StatusDonut';

/**
 * Diagramm-Karte des KI-Assistenten: Kin liefert nur strukturierte Daten
 * ({ typ, titel, einheit, daten: [{label, wert}] }), gerendert wird hier
 * mit den App-eigenen Chart-Bausteinen — Farbsystem und Dark Mode
 * kommen automatisch mit (Balken/Donut: Indigo-Ramp, Linie: Teal).
 */

// Ramp-Stufen für mehrfarbige Segmente (Donut) — eine Familie, kein Kirmesrad
const DONUT_STEPS = [500, 300, 700, 200, 900, 100];

export default function KinChart({ chart }) {
  const theme = useTheme();
  const t = theme.palette.tokens;
  const daten = chart.daten || [];

  let inhalt = null;
  if (chart.typ === 'balken') {
    const hoehe = Math.max(120, daten.length * 30 + 50);
    inhalt = (
      <Box sx={{ height: hoehe }}>
        <ResponsiveBar
          theme={nivoTheme(theme)}
          data={[...daten].reverse()}
          keys={['wert']}
          indexBy="label"
          layout="horizontal"
          margin={{ top: 4, right: 24, bottom: 28, left: 104 }}
          padding={0.25}
          colors={[t.ramp[500]]}
          borderRadius={3}
          labelTextColor={theme.palette.mode === 'dark' ? '#14181F' : '#FFFFFF'}
          axisBottom={{ tickSize: 0, tickPadding: 6 }}
          axisLeft={{ tickSize: 0, tickPadding: 6 }}
          enableGridY={false}
          enableGridX
          animate={false}
        />
      </Box>
    );
  } else if (chart.typ === 'linie') {
    inhalt = (
      <Box sx={{ height: 180 }}>
        <ResponsiveLine
          theme={nivoTheme(theme)}
          data={[{ id: chart.titel || 'Verlauf', data: daten.map((d) => ({ x: d.label, y: d.wert })) }]}
          margin={{ top: 12, right: 16, bottom: daten.length > 6 ? 44 : 30, left: 36 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 0, max: 'auto' }}
          curve="monotoneX"
          colors={[t.accent]}
          lineWidth={2}
          enableArea
          areaOpacity={0.1}
          areaBaselineValue={0}
          pointSize={6}
          pointColor={t.accent}
          pointBorderWidth={2}
          pointBorderColor={t.surface}
          enableGridX={false}
          gridYValues={4}
          axisBottom={{ tickSize: 0, tickPadding: 8, tickRotation: daten.length > 6 ? -35 : 0 }}
          axisLeft={{ tickSize: 0, tickPadding: 6, tickValues: 4 }}
          useMesh
          animate={false}
        />
      </Box>
    );
  } else if (chart.typ === 'trichter') {
    // Pipeline-Trichter im Stil der FunnelTile: Balkenbreite relativ zur
    // größten Stufe, Ramp-Stufen von hell nach satt
    const max = Math.max(...daten.map((d) => d.wert), 1);
    const stufen = [200, 300, 500, 700, 900];
    inhalt = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, py: 0.5 }}>
        {daten.map((d, i) => (
          <Box key={d.label} sx={{ display: 'grid', gridTemplateColumns: '96px 1fr 30px', alignItems: 'center', gap: 1, fontSize: '0.74rem' }}>
            <Typography component="span" noWrap sx={{ fontSize: 'inherit', color: 'text.secondary', fontWeight: 550, textAlign: 'right' }}>
              {d.label}
            </Typography>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{
                height: 13, width: `${Math.max((d.wert / max) * 100, d.wert ? 5 : 0)}%`,
                bgcolor: t.ramp[stufen[Math.min(i, stufen.length - 1)]], borderRadius: '0 4px 4px 0',
              }} />
            </Box>
            <Typography component="span" sx={{ fontSize: 'inherit', color: 'text.primary', fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
              {d.wert}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  } else {
    // Donut über den bestehenden StatusDonut (Zentrum-KPI + Wertelegende)
    const donutDaten = daten.map((d, i) => ({
      id: d.label, label: d.label, value: d.wert,
      color: t.ramp[DONUT_STEPS[i % DONUT_STEPS.length]],
    }));
    inhalt = (
      <Box sx={{ height: 170 }}>
        <StatusDonut data={donutDaten} centerLabel={chart.einheit || 'Gesamt'} />
      </Box>
    );
  }

  return (
    // flexShrink 0: im überlaufenden Chat-Verlauf nie zusammenquetschen
    <Box sx={{ ml: '30px', maxWidth: '92%', flexShrink: 0, border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, py: 0.75, bgcolor: t.surface2, display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Typography sx={{ fontWeight: 650, fontSize: '0.76rem', color: 'text.secondary' }} noWrap>
          {chart.titel || 'Diagramm'}
        </Typography>
        {chart.einheit && chart.typ !== 'donut' && (
          <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>in {chart.einheit}</Typography>
        )}
      </Box>
      <Box sx={{ p: 1, pt: 1.25 }}>{inhalt}</Box>
    </Box>
  );
}
